import { useEffect, useRef, useState } from 'react';

// Components
import { Tabs, Tab } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { Stack } from '@raptormaps/layout';
import { Text } from '@raptormaps/text';
import { LegacyRoles } from './LegacyRoles';
import { NewRoles } from './NewRoles';

// API/Models
import { RAPTOR_X_ORG_ID } from '@raptormaps/raptor-lib';
import RestApiV2 from '../../api/RestApiV2';
import { Org } from '@raptormaps/raptor-models';
import { useApi } from '../../hooks/useApi';
import { AdminApi } from '@raptormaps/raptor-internal-client-ts';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import type { OrgUser } from '../../api/types/OrgUser';
import { AlertModal } from '@raptormaps/alert-modal';
import {
  useInvalidateGlobalUserRoles,
  type GlobalUserRoleUpdate,
} from '../../hooks/useRoles';

const RolesViewer = () => {
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [orgs, setOrgs] = useState([]);
  const [changedPermissions, setChangedPermissions] = useState<
    GlobalUserRoleUpdate[]
  >([]);
  const [selectOrg, setSelectOrg] = useState<number>(RAPTOR_X_ORG_ID);
  const [activeTab, setActiveTab] = useState<string>('legacy');
  const [newRolesKey, setNewRolesKey] = useState<number>(0);
  const triggerAlertModalRef = useRef<() => void>(() => null);
  const api = new RestApiV2();
  const AdminApiClient = useApi(AdminApi, {
    basePath: window.REACT_APP_API_ENDPOINT_V2,
  });
  const { invalidateGlobalUserRoles } = useInvalidateGlobalUserRoles();

  const isNewRolesEnabled = useFeatureIsOn('new-roles-tab');

  /**
   * Gets all orgs
   * api - api_v2_raptor_admin_get_all_orgs
   */
  const getOrgs = () => {
    api
      .get('raptor_admin/orgs', { params: { populate: 0 } })
      .then(res => {
        setOrgs(res.orgs.map((o: Org) => Org.initFromDBOrg(o)));
      })
      .catch(err => {
        console.log(err);
      });
  };

  /**
   * Gets all users for the given org id
   */
  const getOrgUsers = () => {
    AdminApiClient.adminGetAllOrgUsers({
      org_id: selectOrg,
    }).then(res => {
      setUsers(res.users as OrgUser[]);
    });
  };

  useEffect(() => getOrgs(), []);
  useEffect(() => getOrgUsers(), [selectOrg]);

  return (
    <Stack className="roles-viewer">
      <Stack gutter="xs" className="header">
        <Text variant="label">Filter by Org</Text>
        <Typeahead
          id="org"
          options={Object.values(orgs)}
          placeholder="Type to Search Orgs..."
          labelKey={(org: Org) => `${org.name}`}
          onChange={selected =>
            selected.length > 0 && setSelectOrg(selected[0].id)
          }
          defaultInputValue="RaptorX"
        />
      </Stack>

      {/* @ts-ignore */}
      <Tabs activeKey={activeTab} onSelect={k => setActiveTab(k)}>
        <Tab eventKey="legacy" title="Legacy Roles">
          <LegacyRoles users={users} setUsers={setUsers} />
        </Tab>
        {isNewRolesEnabled ? (
          <Tab
            eventKey="new"
            title="New Roles"
            onExiting={() => {
              if (changedPermissions.length) {
                triggerAlertModalRef.current();
                // stay on the new roles tab until confirming
                setActiveTab('new');
              }
            }}
          >
            <NewRoles
              key={newRolesKey}
              users={users}
              changedPermissions={changedPermissions}
              setChangedPermissions={setChangedPermissions}
            />
          </Tab>
        ) : null}
      </Tabs>

      <AlertModal
        title="Your progress will be lost."
        description="Are you sure? You'll lose your progress if you exit this tab."
        triggerRef={triggerAlertModalRef}
        onConfirm={() => {
          setChangedPermissions([]);
          invalidateGlobalUserRoles();
          setNewRolesKey(prev => prev + 1); // Force NewRoles to remount with fresh data
          setActiveTab('legacy');
        }}
        confirmBtnText="Confirm"
      />
    </Stack>
  );
};

export default RolesViewer;
